import type { FC } from "react";
import { memo, useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { DEPRECATED_Loading } from "@ui/components/DEPRECATED_Loading";
import { useFormContext } from "@ui/components/Form";
import { Txt } from "@ui/components/Txt";
import { useQueryStringParams } from "@ui/hooks/useQueryStringParams";
import type { AsyncAvData } from "@ui/hooks/useSessionAsyncAv";
import { useSessionAsyncAv } from "@ui/hooks/useSessionAsyncAv";
import passportImage from "@ui/images/passport.png";
import { useTranslations } from "@ui/providers/useTranslations";
import { getBackendErrorCode, getBackendErrorMessage } from "@ui/utils/backendError";
import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { Button } from "@jti/core-components/components/Button";
import { toast } from "@jti/core-components/components/Toaster";
import {
  getAgeVerificationUuid,
  prepareAddressForAv,
  removeAgeVerificationUuid,
  removeCookie,
  setAgeVerificationUuid,
  translateBackendErrorCode,
} from "@jti/fe-common";
import { useAvpActions } from "@jti/rest-api";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import type { ValidationMethodsProps } from "../ValidationMethods";
import { useAvMethods } from "../ValidationMethods/hooks/useAvMethods";
import type { AvFormValues } from "../ValidationMethods/interfaces/AvFormValues";
import { MethodSelection } from "../ValidationMethods/MethodSelection/MethodSelection";
import { AvMethodsComponents, SyncAvWidgets } from "../ValidationMethods/SyncAvWidgets/SyncAvWidgets";

import { AsyncValidationPopup } from "./AsyncValidationPopup/AsyncValidationPopup";
import { FieldsArray } from "./FieldsArray/FieldsArray";

import styles from "./ValidationMethods.module.scss";

const { sanitize } = dompurify;

const formPrefix = "avpValues";
const adultoCookieKey = "adultocz_local";
const VERIFF_AV = "veriff_av";
const uuidCookieTtl = 30 * 60; // 30 minutes - the same as the session timeout for BE for AV provider resposne - possible to change in the future if needed based on Market feedback

const getImagePath = (imagePath: unknown): string => {
  if (typeof imagePath === "string") {
    return imagePath;
  }
  if (typeof imagePath === "object" && imagePath !== null && "src" in imagePath && typeof imagePath.src === "string") {
    return imagePath.src;
  }
  return "";
};

/**
 * @deprecated use ValidationMethods from @jti/ui instead
 */
export const DEPRECATED_ValidationMethods: FC<Omit<ValidationMethodsProps, "navigation">> = memo(
  ({
    selectedMethod,
    selectMethod,
    isOptional,
    isPending,
    selectedMethodImage,
    userData = {},
    showImage = true,
    onSuccess,
    onFail,
    buttonClassName,
    buttonLabel,
    children,
    isDisabled,
    baseUrl = window.location.href,
    onLoadDataFromCache,
    onSaveDataToCache,
  }) => {
    const config = useConfig();
    const { __, dictionary } = useTranslations();
    const { search } = useLocation();
    const history = useHistory();
    const querySearch = useQueryStringParams();
    const { getAsyncAvData, saveAsyncAvData, clearAsyncAvData } = useSessionAsyncAv();
    const [isAsyncPendingPopupOpened, setAsyncPendingPopupOpened] = useState(false);
    const [isValidationError, setIsValidationError] = useState(false);
    const [savedAsyncAvData, setSavedAsyncAvData] = useState<AsyncAvData | null>(null);
    const [isButtonDisabledForWidgetVerification, setIsButtonDisabledForWidgetVerification] = useState(true);
    const form = useFormContext<AvFormValues>();
    const {
      verify,
      initAsyncVerification,
      asyncVerificationPending,
      asyncVerificationData,
      getAsyncMethodConfig,
      loadAsyncAgeVerificationScript,
      isVerifing,
      isAsyncVerificationFailed,
    } = useAvpActions();

    // @TODO: Fix the passportImage fallback issue where it's not returning the expected path to the image file as a string.
    const fallbackAgeVerificationImage = config.AV_PHOTO || passportImage;
    const imagePath = selectedMethodImage || fallbackAgeVerificationImage || "";
    const imageProps = getImagePath(imagePath);

    const { method, onSelectMethod, availableMethods, isAvailableMethodsLoading } = useAvMethods({
      selectedMethod,
      selectMethod,
    });

    useEffect(() => {
      const isSelectedMethodWidget = (method: string) => Object.hasOwn(AvMethodsComponents, method);

      if (selectedMethod) {
        setIsButtonDisabledForWidgetVerification(isSelectedMethodWidget(selectedMethod));
      }
    }, [selectedMethod]);

    useEffect(() => {
      if (asyncVerificationData) {
        const { status } = asyncVerificationData.data?.response_body || {};
        form.setValue("avpValues.request_uuid", asyncVerificationData.uuid);
        setAgeVerificationUuid(asyncVerificationData.uuid, uuidCookieTtl);

        if (status === "success") {
          onVeriffResponse();
        } else {
          const workflowName = getAsyncMethodConfig(selectedMethod)?.workflowName;
          const workflowDefinitionId = getAsyncMethodConfig(selectedMethod)?.workflowDefinitionId;
          form.setValue("avpValues.workflowName", workflowName ?? "");
          form.setValue("avpValues.workflowDefinitionId", workflowDefinitionId ?? "");

          loadAsyncAgeVerificationScript(selectedMethod, asyncVerificationData, {
            token: asyncVerificationData.data?.token,
            onSuccess: () => {
              asyncAgeVerify({
                ageVerificationMethodCode: selectedMethod,
                dateOfBirth: userData?.dateOfBirth ?? "",
                email: userData.email ?? "",
                firstName: userData?.firstname ?? "",
                lastName: userData?.lastname ?? "",
                request_uuid: asyncVerificationData.uuid,
                sessionUrl: "",
                workflowDefinitionId: workflowDefinitionId ?? "",
                workflowName: workflowName ?? "",
              });
            },
          });
        }
      }
    }, [asyncVerificationData]);

    useEffect(() => {
      const asyncAvData = getAsyncAvData();
      if (
        querySearch.redirectedFromAsyncMethod &&
        !Array.isArray(querySearch.redirectedFromAsyncMethod) &&
        asyncAvData
      ) {
        selectMethod(querySearch.redirectedFromAsyncMethod);
        setSavedAsyncAvData(asyncAvData);
        asyncAgeVerify(asyncAvData);
        setAsyncPendingPopupOpened(true);
        clearAsyncAvData();
        onLoadDataFromCache?.(asyncAvData);
        // Clear url after redirection
        history.replace({
          search: "",
        });
      }
    }, [search, selectMethod]);

    useEffect(() => {
      dispatchTrackingEvent(TrackingEvents.TrackingHardAgeVerificationStart);
    }, []);

    const clearWidgetAvData = useCallback(() => {
      removeCookie(adultoCookieKey);
    }, []);

    useEffect(() => {
      return () => {
        clearWidgetAvData();
      };
    }, [clearWidgetAvData]);

    const asyncAgeVerify = async ({
      ageVerificationMethodCode,
      dateOfBirth = "",
      email,
      firstName = "",
      lastName = "",
      request_uuid,
      workflowName,
      workflowDefinitionId,
    }: AsyncAvData) => {
      try {
        const {
          uuid: avpUuid,
          is_verified,
          status_from_provider,
        } = await verify({
          user_email: email,
          country_code: config.ISO_COUNTRY_CODE,
          method_code: ageVerificationMethodCode,
          fields: {
            firstname: firstName,
            lastname: lastName,
            date_of_birth: dateOfBirth,
            request_uuid,
            workflow_name: workflowName,
            workflow_definition_id: workflowDefinitionId,
          },
        });
        if (status_from_provider === "pending") {
          setAsyncPendingPopupOpened(true);
          return;
        }

        if (status_from_provider === "failed") {
          onAgeVerificationFail();
          return;
        }

        if (status_from_provider === "success") {
          setAsyncPendingPopupOpened(false);
        }
        onAgeVerification(avpUuid, is_verified);
      } catch {
        onAgeVerificationFail();
      }
    };

    const verifyAge = async (formValues: AvFormValues, verificationMethodCode: string) => {
      try {
        const { firstname, lastname, dateOfBirth, phone, email, addresses } = userData;

        const { uuid: avpUuid, is_verified } = await verify({
          user_email: email,
          country_code: config.ISO_COUNTRY_CODE,
          method_code: verificationMethodCode,
          fields: {
            ...formValues.avpValues,
            ...(userData?.additionalData || {}),
            firstname,
            lastname,
            date_of_birth: dateOfBirth,
            phone,
            ...prepareAddressForAv(addresses),
          },
        });

        if (is_verified) {
          dispatchTrackingEvent(TrackingEvents.TrackingHardAgeVerificationSuccess, {
            hav_method: verificationMethodCode,
          });
        }

        onAgeVerification(avpUuid, is_verified);
      } catch (error) {
        const errorMessage = translateBackendErrorCode(
          getBackendErrorCode(error),
          getBackendErrorMessage(error),
          dictionary
        );

        onAgeVerificationFail(errorMessage);
      }
    };

    const isFormFilled = () => Object.values(form.getValues()?.avpValues || {}).some(Boolean);

    const onAgeVerification = async (avpUuid: string, is_verified: boolean) => {
      if (avpUuid && is_verified) {
        removeAgeVerificationUuid();
        clearWidgetAvData();
        onSuccess?.(avpUuid);
      } else if ((isOptional && isFormFilled()) || (!isOptional && !is_verified)) {
        onAgeVerificationFail();
      }
    };

    const onAgeVerificationFail = (message?: string) => {
      removeAgeVerificationUuid();
      setIsValidationError(true);
      toast.error(message || __("auth.age_verification.result.message_fail"));
      onFail?.(message);
    };

    const onVeriffResponse = () => {
      if (asyncVerificationData) {
        const { status, verification } = asyncVerificationData.data?.response_body || {};
        form.setValue("ageVerificationMethodCode", selectedMethod);

        if (status === "success" && verification?.url) {
          saveAsyncAvData({
            ageVerificationMethodCode: selectedMethod,
            dateOfBirth: userData.dateOfBirth ?? "",
            email: userData.email ?? "",
            firstName: userData.firstname ?? "",
            lastName: userData.lastname ?? "",
            request_uuid: asyncVerificationData.uuid,
            sessionUrl: verification.url,
          });
          onSaveDataToCache?.();
          window.location.href = verification.url;
        }
      }
    };

    const initAsyncMethod = (selectedMethod: string) => {
      return initAsyncVerification({
        methodCode: selectedMethod,
        userEmail: userData?.email || "",
        fields: {
          callback: `${baseUrl}?redirectedFromAsyncMethod=${selectedMethod}`,
          firstName: userData?.firstname || "",
          lastName: userData?.lastname || "",
          dateOfBirth: userData?.dateOfBirth,
          workflowName: getAsyncMethodConfig(selectedMethod)?.workflowName,
          workflowDefinitionId: getAsyncMethodConfig(selectedMethod)?.workflowDefinitionId,
        },
      });
    };

    // additional verification for async methods - motivated by veriff issue with long response time
    const handleCheckPrevAsyncVerification = async () => {
      const uuidFromCookie = getAgeVerificationUuid();

      if (!uuidFromCookie) {
        return false;
      }

      try {
        const { is_verified, status_from_provider } = await verify({
          user_email: userData.email ?? "",
          country_code: config.ISO_COUNTRY_CODE,
          method_code: selectedMethod,
          fields: {
            firstname: userData.firstname,
            lastname: userData.lastname,
            date_of_birth: userData.dateOfBirth,
            request_uuid: uuidFromCookie,
            workflow_name: getAsyncMethodConfig(selectedMethod)?.workflowName,
            workflow_definition_id: getAsyncMethodConfig(selectedMethod)?.workflowDefinitionId,
          },
        });

        if (status_from_provider === "success" && is_verified) {
          toast.success(__("auth.age_verification.result.message_success.prev_uuid_verification"));
          onAgeVerification(uuidFromCookie, is_verified);
          return true;
        }
      } catch {
        removeAgeVerificationUuid();
        return false;
      }
    };

    const triggerVerification = async () => {
      const formValues = form.getValues();
      const verificationMethod = availableMethods.find(({ code }) => code === selectedMethod);
      if (isOptional && !isFormFilled() && verificationMethod?.type !== "async") {
        clearWidgetAvData();
        return onSuccess?.();
      }
      const isValid = await form.trigger();

      if (!isValid) {
        return;
      }

      const isVerificationSkipped = await handleCheckPrevAsyncVerification();

      if (isVerificationSkipped) {
        return;
      }

      if (verificationMethod?.type === "async") {
        try {
          await initAsyncMethod(selectedMethod);
        } catch {
          onAgeVerificationFail();
        }
      } else {
        await verifyAge(formValues, selectedMethod);
      }
    };

    const tryAgainVerification = () => {
      if (savedAsyncAvData) {
        setIsValidationError(false);
        asyncAgeVerify(savedAsyncAvData);
      } else {
        setAsyncPendingPopupOpened(false);
      }
    };

    const returnToAsyncVerificationProcess = () => {
      if (selectedMethod === VERIFF_AV) {
        setAsyncPendingPopupOpened(false);
        triggerVerification();
        return;
      }
      const verificationUrl = savedAsyncAvData?.sessionUrl;
      if (verificationUrl) {
        setIsValidationError(false);
        saveAsyncAvData(savedAsyncAvData);
        onSaveDataToCache?.();
        window.location.href = verificationUrl;
      } else {
        setAsyncPendingPopupOpened(false);
        setIsValidationError(false);
        triggerVerification();
      }
    };

    const cancelAsyncAgeVerificationProcess = () => {
      setAsyncPendingPopupOpened(false);
    };

    const handleSyncAvWidgetVerification = (verificationId: string) => {
      form.setValue("avpValues.verification_id", verificationId);
      setIsButtonDisabledForWidgetVerification(false);
    };

    if (isAvailableMethodsLoading) {
      return <DEPRECATED_Loading dataTestId="ageVerificationAvailableMethodsLoader" />;
    }

    return (
      <>
        {availableMethods?.length ? (
          <div data-testid="ageVerificationMethods" className={styles.formContainer}>
            <div className={styles.methodsRow}>
              <div className={styles.methodsContainer}>
                {(showImage || availableMethods.length > 1) && (
                  <div className={styles.identificationLabelContainer}>
                    <Txt variant="small-text" className={styles.methodsTitle}>
                      {__("auth.age_verification.identification_document_label")}
                    </Txt>
                  </div>
                )}

                <MethodSelection
                  selectMethod={onSelectMethod}
                  methods={availableMethods}
                  selectedMethod={selectedMethod}
                />
              </div>
              {showImage && (
                <img
                  className={styles.verificationImage}
                  data-testid="ageVerificationImage"
                  alt={__("auth.age_verification.passport_image_alt")}
                  src={imageProps}
                />
              )}
            </div>
            {method && method.type !== "async" && (
              <FieldsArray {...method} isOptional={isOptional} fieldName={(name: string) => `${formPrefix}.${name}`} />
            )}

            <SyncAvWidgets selectedMethod={selectedMethod} onAvFinished={handleSyncAvWidgetVerification} />

            <AsyncValidationPopup
              isOpened={isAsyncPendingPopupOpened}
              onCancel={cancelAsyncAgeVerificationProcess}
              onTryAgain={returnToAsyncVerificationProcess}
              onRefresh={tryAgainVerification}
              isError={isValidationError || isAsyncVerificationFailed}
            />
            {children}
            <div className={classNames(styles.actionContainer, buttonClassName)}>
              <Button
                pending={isPending || isVerifing || asyncVerificationPending}
                disabled={isDisabled || isButtonDisabledForWidgetVerification}
                variant="primary"
                data-testid="ageVerificationSubmitButton"
                onClick={() => triggerVerification()}
              >
                {buttonLabel || __("profile.age_verification.submit")}
              </Button>
            </div>
          </div>
        ) : (
          <p>{__("auth.age_verification.no_services_message")}</p>
        )}
        <div
          dangerouslySetInnerHTML={{
            __html: sanitize("<transactionlink-widget></transactionlink-widget>", {
              ALLOWED_TAGS: ["transactionlink-widget"],
            }),
          }}
        ></div>
      </>
    );
  }
);
